import React, {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { array, bool, func, string } from 'prop-types';
import cn from 'classnames';
import { DrawingZone } from 'components/DrawingZone';
import Fullscreen from 'components/Fullscreen.js';
import PanelWrapperAnimation from './PanelWrapperAnimation.jsx';
import style from './panel.module.css';
import ZoneSizeCompensator from 'components/DrawingZone/SizeCompensator.jsx';

export default PanelWrapper;

// not sure, that it should be here
// margin for player controls
const MIN_MARGIN = {
  web: 100,
  mobile: 130,
};

PanelWrapper.propTypes = {
  emojiArray: array.isRequired,
  closePanel: func.isRequired,
  isShow: bool,
  devicePlatform: string,
  panelOrientation: string,
};

PanelWrapper.defaultProps = {
  emojiArray: [],
};

PanelWrapper.displayName = 'PanelWrapper';

const computedStyles = getComputedStyle(document.documentElement);

function PanelWrapper(props) {
  const {
    isShow,
    emojiArray,
    devicePlatform,
    panelOrientation,
    closePanel,
    children,
  } = props;

  const [isRender, setIsRender] = useState(false);
  const [viewHeight, setViewHeight] = useState(
    document.documentElement.clientHeight,
  );

  const isFullscreenMode = useMemo(
    () =>
      resolvePanelFullscreenMode(viewHeight, devicePlatform, emojiArray.length),
    [emojiArray.length, devicePlatform, viewHeight],
  );

  const wrapperClassNames = useMemo(
    () =>
      cn({
        [style.panelWrapper]: !isFullscreenMode,
        [style.panelFullscreenWrapper]: isFullscreenMode,
        [style.wrapperVertical]: panelOrientation === 'vertical',
        [style.wrapperHorizontal]: panelOrientation === 'horizontal',
      }),
    [isFullscreenMode, panelOrientation],
  );

  const setCurrentHeight = useCallback(() => {
    setViewHeight(document.documentElement.clientHeight);
  }, []);

  const onAnimationExited = useCallback(() => {
    setIsRender(false);
  }, []);

  const hidePanel = useCallback(() => {
    closePanel();
    setIsRender(false);
  }, [closePanel]);

  useEffect(() => {
    if (isShow && !isRender) {
      setIsRender(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow]);

  useEffect(() => {
    if (!isRender) {
      return;
    }

    window.addEventListener('orientationchange', hidePanel);
    window.addEventListener('resize', setCurrentHeight);

    return () => {
      window.removeEventListener('orientationchange', hidePanel);
      window.removeEventListener('resize', setCurrentHeight);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRender]);

  if (!isRender) {
    return null;
  }

  return isFullscreenMode ? (
    <Fullscreen portal="#emoji_panel" onCloseRequest={hidePanel}>
      <PanelWrapperAnimation
        isShow={isShow}
        className={wrapperClassNames}
        onExited={onAnimationExited}
        onHidePanel={hidePanel}
      >
        {cloneElement(children, { isFullscreen: true })}
      </PanelWrapperAnimation>
    </Fullscreen>
  ) : (
    <DrawingZone zone_id="safe_zone" className={style.safeZone}>
      <ZoneSizeCompensator mode={'vertical'}>
        <PanelWrapperAnimation
          isShow={isShow}
          className={wrapperClassNames}
          onExited={onAnimationExited}
        >
          {children}
        </PanelWrapperAnimation>
      </ZoneSizeCompensator>
    </DrawingZone>
  );
}

function resolvePanelFullscreenMode(height, platform, emojiAmount = 0) {
  const buttonSize = parseInt(computedStyles.getPropertyValue('--button-size'));
  const margin = MIN_MARGIN[platform] || MIN_MARGIN.web;

  return height < buttonSize * emojiAmount + margin;
}
